import { Container, RoadmapPhase, Section, Stack } from "@humaniti/ui";
import { Link } from "react-router-dom";
import { SEO } from "../lib/seo";

const PHASES = [
  {
    numeral: "I",
    noun: "The Network",
    tagline: "Verified Humans. Verified work. Direct pay.",
    body: "A marketplace where vetted Humans complete work, peer-consensus verifies it, and Builders pay directly per verified label. The first phase of Humaniti — the substrate everything else builds on.",
    bullets: [
      "17 task types — image, text, audio, video, 3D, document.",
      "N-Human consensus per label (default 3, configurable 1 / 3 / 5 / 7). Disputes route to Stewards.",
      "Tier-gated qualification — T1 phone, T2 ID, T3 reputation + Steward eligibility.",
      "Direct pay per verified label. Payouts via Wise, Razorpay, Rapyd, Stripe Connect, fiat → USDC.",
      "Transparent network fee. No negotiation, no carve-outs.",
      "Web app for Humans + Builders. Native Android (Kotlin Multiplatform).",
    ],
    pullQuote:
      "Frontier AI runs on data labeled by people. Today that labor is invisible, the verification is opaque, the datasets are private. The Network inverts that. Labor is paid directly. Verification is consensus, not vendor word. Every label has a Human's name on it.",
  },
  {
    numeral: "II",
    noun: "The Record",
    tagline: "Datasets that don't disappear.",
    body: "Every dataset becomes a signed, addressable, permanent record. The provenance of each label is portable. Humans get on-chain attestations for what they verified.",
    bullets: [
      "On-chain dataset attestations. Anyone can verify provenance without trusting Humaniti.",
      "Per-label signing. Each label carries a signed receipt of the Human(s) who produced and verified it.",
      "Soulbound contribution receipts. A Human's verified work history travels with them.",
      "Dataset NFTs (where licensing model permits). Transparent revenue split rules.",
      "Long-term storage with redundant pinning. Datasets do not depend on Humaniti's continued operation.",
    ],
    pullQuote:
      "Knowledge has a habit of vanishing into private databases. The Record makes the work permanent. The dataset belongs to the Network — not to a vendor, not to a single buyer, not to whoever has the latest backup. If Humaniti ceases to exist tomorrow, the work survives.",
  },
  {
    numeral: "III",
    noun: "The Economy",
    tagline: "Humans earn equity in the work.",
    body: "The network has its own credit system. Humans stake what they've earned to multiply their pay rate, unlock higher-tier work, and qualify for Steward roles. The work becomes ownership.",
    bullets: [
      "A native credit unit (HMNT). Earned through verified work. Held in-network or withdrawn to fiat.",
      "Staking tiers. Higher stake = higher pay multiplier + access to higher-stakes tasks. Slashing penalises gaming.",
      "Work Credits — a stable, USD-pegged work-unit derivative for Builder pre-funding.",
      "Delegated staking. Humans can delegate to Stewards and share in the multiplier.",
      "Retroactive distribution to early Humans — pre-Phase-3 verified work earns a credit allocation.",
    ],
    pullQuote:
      "In every other labor market, you sell your time and walk away with cash. The Economy phase asks a different question — what if the work you did stayed yours? Verified Humans accumulate credits that compound. The longer you build the Network, the more it becomes yours.",
  },
  {
    numeral: "IV",
    noun: "The Commons",
    tagline: "The network governs itself.",
    body: "Humans + Builders set the rules together, fund what matters, share the surplus. Humaniti the company becomes a steward, not an owner. Annotation belongs to those who annotate.",
    bullets: [
      "Network governance. Decisions on fees, thresholds, dispute outcomes, protocol upgrades — by on-chain vote weighted by verified work + stake.",
      "Sub-DAOs. Quality Council, Language Guilds, Domain Experts, Worker Welfare, Treasury, Disputes, Platform Dev, Growth.",
      "Annotation DAOs. Collectives of Humans co-own the datasets they verify; royalties flow to contributors.",
      "Retroactive Public Goods Funding. Surplus funds open datasets, tooling, education, accessibility.",
      "Worker welfare fund. Hardship grants, healthcare matching, retraining funded from network surplus.",
    ],
    pullQuote:
      "A network that depends on Humans should be governed by Humans. The Commons phase finishes what the Network started — labor that owns the rules of its own labor. Humaniti the company becomes one voice among many. The work, the record, the economy, and the rules all belong to the people who showed up and verified the labels.",
  },
];

export const RoadmapPage = () => (
  <>
    <SEO
      title="Roadmap — Network, Record, Economy, Commons"
      description="The Humaniti arc. Four phases, no timelines. The labor → ownership → governance through-line of how the Network evolves."
      path="/roadmap"
    />
    <Section>
      <Container size="full">
        <Stack gap="2xl">
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-label)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "var(--fg-secondary)",
            }}
          >
            § The Humaniti arc
          </span>
          {/* The arc string ("Network → Record → Economy → Commons.") is the
              longest hero we ship — at the standard --fs-display (76px) it
              needs ~1509px in one line. The Container size="full" caps the
              parent at ~1232px regardless of viewport, so the cap can't track
              vw — it has to track the actual parent width. clamp(40px, 4.3vw,
              60px) lands at exactly the size that fits 1232px in one line
              (60px → ~1191px) at every desktop width ≥ ~1395px viewport, and
              scales down smoothly at narrower widths (laptop / tablet).
              text-wrap: balance is the final safety net for the still-wrapped
              case at very narrow viewports. */}
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-sans)",
              fontWeight: 500,
              fontSize: "clamp(40px, 4.3vw, 60px)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              textWrap: "balance",
            }}
          >
            Network → Record → Economy → Commons<span style={{ color: "var(--teal)" }}>.</span>
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-body-lg)",
              lineHeight: "var(--lh-body-lg)",
              maxWidth: 720,
              color: "var(--fg-secondary)",
            }}
          >
            Four phases. No timelines. The arc tells the labor → ownership → governance
            through-line.
          </p>
        </Stack>
      </Container>
    </Section>

    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        {PHASES.map((p) => (
          <RoadmapPhase key={p.numeral} {...p} />
        ))}
      </Container>
    </Section>

    <Section style={{ borderTop: "1px solid var(--slate-300)" }}>
      <Container size="full">
        <Stack gap="md" alignItems="center" style={{ textAlign: "center" }}>
          <p style={{ margin: 0, color: "var(--fg-secondary)" }}>
            Why this matters in our own words —
          </p>
          <Link to="/manifesto">Read the Manifesto →</Link>
        </Stack>
      </Container>
    </Section>
  </>
);
